import styled from "styled-components";
import {AnimatePresence, motion} from "framer-motion";
import {Button} from "../../components/Button";
import React, {useState, useEffect} from "react";
import {useAppContext} from "../../provider/AppProvider";
import {Item} from "./Item";
import {useYandexSdk} from "../../provider/yandex/yandexSdk";
import {useViewPortState} from "../../provider/ResizeProvider";
import {Slider} from "../../components/Swiper/Slider";

export interface ILeaderEntity {
    score: number;
    rank: number;
    player: {
        publicName: string;
        getAvatarSrc: (prop: string) => string;
        scopePermissions: {
            avatar: string;
            public_name: string;
        },
        uniqueID: string;
    },
    formattedScore: string;
}

export interface ILeader {
    entries: ILeaderEntity[]
}

const Root = styled.div`
    position: fixed;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    z-index: 443213123;
`;

const Background = styled(motion.div)`
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: 100%;
    background: rgba(65, 67, 73, 0.9);
    z-index: -1;
`;

const variants = {
    open: {
        clipPath: `circle(4000px at 120px 120px)`,
        transition: {
            type: "spring",
            stiffness: 40,
            restDelta: 1,
        }
    },
    closed: {
        clipPath: "circle(30px at 40px 40px)",
        transition: {
            delay: 0.4,
            type: "spring",
            stiffness: 400,
            damping: 40,
        },
    },
};

const variantsList = {
    open: {
        transition: {staggerChildren: 0.07, delayChildren: 0.2},
    },
    closed: {
        transition: {staggerChildren: 0.01, staggerDirection: -1},
    },
};

const variantsHeader = {
    hidden: {
        y: '-100%',
        opacity: 0,
    },
    visible: {
        y: 0,
        opacity: 1,
        transition: {
            delay: 0.3,
            duration: 0.3,
            ease: "easeOut",
        },
    },
    close: {
        y: '-100%',
        opacity: 0,
        transition: {
            duration: 0.3,
            ease: "easeOut",
        },
    }
};

interface Props {
    isOpen: boolean;
}

const objTestPlayer: ILeaderEntity = {
    score: 10,
    rank: 3,
    player: {
        publicName: 'adsdsadas',
        getAvatarSrc: (p: string) => '',
        scopePermissions: {
            avatar: '',
            public_name: '',
        },
        uniqueID: `${Math.random()}`,
    },
    formattedScore: "10",
}

const leaderList = {
    entries: [objTestPlayer, objTestPlayer, objTestPlayer, objTestPlayer, objTestPlayer, objTestPlayer, objTestPlayer, objTestPlayer, objTestPlayer, objTestPlayer, objTestPlayer, objTestPlayer, objTestPlayer, objTestPlayer, objTestPlayer, objTestPlayer, objTestPlayer, objTestPlayer, objTestPlayer]
}

const Header = styled(motion.div)`
    display: flex;
    align-items: center;
    padding: 8px 16px 0 16px;
`;

const ButtonWrapper = styled(motion.div)`
    width: 50px;
    height: 50px;
    
    @media (min-width: 768px) {
        width: 65px;
        height: 65px;
    }
`;

const Title = styled(motion.p)<{leaderBordIndex: number}>`
    font-size: 16px;
    margin-left: 16px;
    font-weight: 900;
    text-shadow: -2px -2px 0 rgb(138 87 234), 2px -2px 0 rgb(138 109 230), -2px 2px 0 rgb(122 103 203), 2px 2px 0 rgb(167 143 236), -3px 0 0 rgb(126 122 217), 3px 0 0 rgb(125 119 214), 0 -3px 0 rgb(111 103 174), 0 3px 0 rgb(124 112 200);
    color: #1c188c;
    filter: invert(1);
    min-width: 163px;
    text-align: center;
    
    @media (min-width: 768px) {
        font-size: 26px;
    }
    
    @media (min-width: 1024px) {
        margin-left: 42px;
    }
`;

const List = styled(motion.div)`
    gap: 12px;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    padding: 16px 12px;
    position: relative;
    margin-top: 24px;
    overflow-y: scroll;
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;  /* Firefox */
    max-height: 80%;

    &::-webkit-scrollbar {
        display: none;
    }
    
    @media (min-width: 1024px) {
        padding: 16px 32px;
    }
`;

const AnimatedTitle = ({ leaderBordIndex, translate }: any) => {
    const titleText = leaderBordIndex ? translate.leadersBomb : translate.leaders;

    return (
            <motion.div
                key={titleText}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1 }}
            >
                <Title leaderBordIndex={leaderBordIndex}>{titleText}</Title>
            </motion.div>
    );
};

export const Leaderbord = ({isOpen}: Props) => {
    const {totalScoreDataLb, totalScoreDataBombLb, player} = useYandexSdk()
    const [isVisible, setVisible] = useState(isOpen);
    const {onSwitchLeaderBord, translate} = useAppContext();
    const {isTableState, isDesktopState} = useViewPortState();
    const [leaderBordIndex, setLbIndex] = useState(0);

    useEffect(() => {
        setVisible(isOpen);
    }, [isOpen]);

    useEffect(() => {
        if (!isVisible) {
            setTimeout(() => {
                onSwitchLeaderBord();
            }, 600)
        }
    }, [isVisible])

    const handleLeaderBoardClose = () => {
        setVisible(false);
    }

    return (
        <Root>
            <Background
                animate={isVisible ? "open" : "closed"}
                variants={variants}
            />
            <Header
                animate={isVisible ? "visible" : "close"}
                initial='hidden'
                variants={variantsHeader}>
                <ButtonWrapper>
                    <Button direction='left' onClick={handleLeaderBoardClose} iconName='chevron' size={isDesktopState || isTableState ? 45 : 30}
                            color='#1c188c'/>
                </ButtonWrapper>
                <AnimatedTitle leaderBordIndex={leaderBordIndex} translate={translate} />
                {/*<Title>{leaderBordIndex ? 'ЛидерБомба' : translate.leaders}</Title>*/}
            </Header>
            {/*<List >*/}
                {/*{totalScoreDataLb.entries.map((item, index) => (*/}
                {/*    <Item item={item} sticky={player && item.player.uniqueID === player._personalInfo.uniqueID} />*/}
                {/*))}*/}
                {/*{totalScoreDataBombLb.entries.map((item, index) => (*/}
                {/*    <Item item={item} sticky={index === leaderList.entries.length - 1} />*/}
                {/*))}*/}

                <Slider setLbIndex={setLbIndex}>
                    <List
                        initial="closed"
                        animate={isVisible ? "open" : "closed"}
                        variants={variantsList}>
                        {totalScoreDataLb.entries.map((item, index) => (
                            <Item key={index} item={item} sticky={player && item.player.uniqueID === player._personalInfo.uniqueID} />
                        ))}
                    </List>
                    <List
                        initial="closed"
                        animate={isVisible ? "open" : "closed"}
                        variants={variantsList}>
                        {totalScoreDataBombLb.entries.map((item, index) => (
                            <Item key={index} item={item} sticky={player && item.player.uniqueID === player._personalInfo.uniqueID} />
                        ))}
                    </List>
                </Slider>
            {/*</List>*/}
        </Root>
    );
}

